import '.d{display:flex;}.ssr-plugin'
import '.e{flex-direction:column;}.ssr-plugin'
import { StaticPageSeo } from '@app/components/core/seo/StaticPageSeo'
import { pages } from '@app/util/links'
import { PageLayout } from '../layout/PageLayout'
import FaqSection from '@app/components/blocks/faq/FaqSection'
import useMediaQuery from '@app/util/useMediaQuery'
import LandingSection from '@app/components/blocks/contentSection/LandingSection'
import LandingContent from '@app/components/blocks/contentSection/blocks/LandingContent'
import { ContactCard } from '@app/pages/info/blocks/ContactCard'
import { Subtitle, Title } from '@app/components/core/typography/Title'
import { styled } from 'inlines'
import { Text } from '@app/components/core/typography/Text'
import Column from '@app/components/layout/Column'
import GroupFormationImage from '@app/assets/images/landing-pages/for-municipalities/group-formation.svg'
import KaartenbakImage from '@app/assets/images/landing-pages/for-municipalities/kaartenbak.svg'
import InsightImage from '@app/assets/images/landing-pages/for-municipalities/insight.svg'
import KnowledgeImage from '@app/assets/images/landing-pages/for-municipalities/kennisdeling.svg'
import { OurPartners } from './blocks/OurPartners'
import ClockIcon from '@app/assets/images/landing-pages/for-municipalities/clock.svg'
import MapIcon from '@app/assets/images/landing-pages/for-municipalities/map.svg'
import PeopleIcon from '@app/assets/images/landing-pages/for-municipalities/people.svg'
import AwardIcon from '@app/assets/images/landing-pages/for-municipalities/award.svg'
import CardsSection, { type CardsSectionItem } from './blocks/CardsSection'
import LandingHero from './blocks/LandingHero'
import ResidentTestimonialsSection, {
  type ResidentTestimonial,
} from './blocks/ResidentTestimonialsSection'
import { useTranslations } from '@i18n/useLanguage'
import { useMemo } from 'react'
import ScrewImage from '@app/assets/images/landing-pages/for-municipalities/testimonials/screw.png'
import CloverImage from '@app/assets/images/landing-pages/for-municipalities/testimonials/clover.png'
import FourLeavesImage from '@app/assets/images/landing-pages/for-municipalities/testimonials/four_leaves.png'

const RESIDENT_TESTIMONIALS: ReadonlyArray<ResidentTestimonial> = [
  {
    title: 'Ze denken heel actief en concreet met ons',
    quote:
      'Voor de werving van nieuwe leden voor ons intergenerationele erf maken we dankbaar gebruik van het platform en de expertise van Crowdbuilding. Het sparren met de medewerkers voelt heel laagdrempelig en persoonlijk. Je merkt dat zij door hun eigen ervaring en kennis goed weten wat er speelt bij een CPO-project. Ze denken heel actief en concreet met ons mee om onze gemeenschap compleet te maken, zodat we samen kunnen bouwen aan de toekomst van Erve Bosco.',
    name: 'Tineke de Haan',
    role: 'CPO Erve Bosco',
    cardStyle: {
      backgroundColor: 'var(--palette-pink-200)',
      backgroundImage: ScrewImage,
    },
    avatarVariant: 'yellow',
  },
  {
    title: 'Hét platform voor wooncommunities',
    quote:
      'CrowdBuilding is voor mij hét platform voor wooncommunities. Het is inspirerend om op één plek te zien dat er veel mensen met een soortgelijke woondroom bezig zijn. Van initiatieven die nog in de ideefase zitten tot initiatieven die aan het bouwen zijn of hun woondroom al (lang) gerealiseerd hebben. Ze zijn allemaal op CrowdBuilding te vinden. Ik gebruik het platform om in contact te komen met gelijkgestemden. En ik hou de agenda in de gaten voor interessante bijeenkomsten.',
    name: 'Natasja Verhoogt',
    role: 'Wooncoöperatie Lydio | Expert',
    cardStyle: {
      backgroundColor: '#E7B095',
      backgroundImage: CloverImage,
    },
    avatarVariant: 'yellow',
  },
  {
    title: 'CrowdBuilding is een verbindende factor',
    quote:
      'Fijn dat CrowdBuilding een verbindende factor speelt tussen collectieve zelfbouwinitiatieven, gemeenten en grondeigenaren. De laagdrempelige kennissessies helpen startende en ervaren initiatieven verder en het platform dat zij bieden laat gelijkgestemden elkaar vinden en in contact brengen. CrowdBuilding zit vol energie, is vernieuwend en denkt mee!',
    name: 'Bas',
    role: 'CPO OLGA door Freunden von Freunden',
    cardStyle: {
      backgroundColor: 'var(--palette-yellow-400)',
    },
    avatarVariant: 'dark-red',
  },
  {
    title: 'Zij kennen alle aspecten van het proces',
    quote:
      'Een CPO proces is soms complex en uitdagend. Het is heel fijn om te sparren met de mensen bij Crowdbuilding. Zij kennen alle aspecten van het proces en kunnen daarom goede adviezen geven.',
    name: 'Bas',
    role: 'CPO OLGA door Freunden von Freunden',
    cardStyle: {
      backgroundColor: 'var(--palette-blue-100)',
      backgroundImage: FourLeavesImage,
    },
    avatarVariant: 'yellow',
  },
]

export const ForMunicipalities = () => {
  const isMobile = useMediaQuery()
  const t = useTranslations()

  const faqItems = useMemo(
    () => [
      {
        question: t.app.forMunicipalities.faq.items.time.question,
        answer: t.app.forMunicipalities.faq.items.time.answer,
      },
      {
        question: t.app.forMunicipalities.faq.items.self.question,
        answer: t.app.forMunicipalities.faq.items.self.answer,
      },
      {
        question: t.app.forMunicipalities.faq.items.live.question,
        answer: t.app.forMunicipalities.faq.items.live.answer,
      },
      {
        question: t.app.forMunicipalities.faq.items.safe.question,
        answer: t.app.forMunicipalities.faq.items.safe.answer,
      },
      {
        question: t.app.forMunicipalities.faq.items.stop.question,
        answer: t.app.forMunicipalities.faq.items.stop.answer,
      },
    ],
    [t],
  )

  const municipalityPainPoints: ReadonlyArray<CardsSectionItem> = useMemo(
    () => [
      {
        title: t.app.forMunicipalities.painPoints.items.noTime.title,
        description:
          t.app.forMunicipalities.painPoints.items.noTime.description,
        imageSrc: ClockIcon,
      },
      {
        title: t.app.forMunicipalities.painPoints.items.enoughInitiatives.title,
        description:
          t.app.forMunicipalities.painPoints.items.enoughInitiatives
            .description,
        imageSrc: PeopleIcon,
      },
      {
        title: t.app.forMunicipalities.painPoints.items.noProcess.title,
        description:
          t.app.forMunicipalities.painPoints.items.noProcess.description,
        imageSrc: MapIcon,
      },
      {
        title: t.app.forMunicipalities.painPoints.items.noExpertise.title,
        description:
          t.app.forMunicipalities.painPoints.items.noExpertise.description,
        imageSrc: AwardIcon,
      },
    ],
    [t],
  )

  const pageMeta = t.app.forMunicipalities.hero

  return (
    <PageLayout gap="6rem">
      <StaticPageSeo
        path={pages.forMunicipalities}
        pageTitle={pageMeta.title}
        description={pageMeta.description}
      />
      <LandingSection>
        <LandingHero
          badge={t.app.forMunicipalities.hero.badge}
          title={t.app.forMunicipalities.hero.title}
          description={t.app.forMunicipalities.hero.description}
        />
      </LandingSection>

      <OurPartners />

      <LandingSection>
        <ResidentTestimonialsSection
          isMobile={isMobile}
          title={t.app.forMunicipalities.residentTestimonials.title}
          intro={t.app.forMunicipalities.residentTestimonials.intro}
          testimonials={RESIDENT_TESTIMONIALS}
        />
      </LandingSection>

      <LandingSection>
        <CardsSection
          isMobile={isMobile}
          title={t.app.forMunicipalities.painPoints.title}
          intro={t.app.forMunicipalities.painPoints.intro}
          items={municipalityPainPoints}
        />
      </LandingSection>

      <Column gap="2.5rem">
        <div className="d e">
          <Title.H2>{t.app.forMunicipalities.solutions.title}</Title.H2>
          <Subtitle style={{ marginBottom: '1rem' }}>
            {t.app.forMunicipalities.solutions.subtitle}
          </Subtitle>
          <Text.Medium style={{ maxWidth: '35rem' }}>
            {t.app.forMunicipalities.solutions.intro}
          </Text.Medium>
        </div>

        <LandingSection backgroundColor="var(--palette-blue-50)">
          <LandingContent
            imageSide="left"
            imageSrc={GroupFormationImage}
            title={t.app.forMunicipalities.solutions.groupGuidance.title}
            subtitle={t.app.forMunicipalities.solutions.groupGuidance.subtitle}
            description={
              t.app.forMunicipalities.solutions.groupGuidance.description
            }
            badge={t.app.forMunicipalities.solutions.groupGuidance.badge}
            isCard={true}
            planDemoButton={true}
          />
        </LandingSection>

        <LandingSection backgroundColor="var(--palette-yellow-200)">
          <LandingContent
            imageSide="right"
            imageSrc={KaartenbakImage}
            title={t.app.forMunicipalities.solutions.kaartenbak.title}
            subtitle={t.app.forMunicipalities.solutions.kaartenbak.subtitle}
            description={
              t.app.forMunicipalities.solutions.kaartenbak.description
            }
            badge={t.app.forMunicipalities.solutions.kaartenbak.badge}
            isCard={true}
            planDemoButton={true}
          />
        </LandingSection>

        <LandingSection backgroundColor="var(--palette-beige-100)">
          <LandingContent
            imageSide="left"
            imageSrc={InsightImage}
            title={t.app.forMunicipalities.solutions.insight.title}
            subtitle={t.app.forMunicipalities.solutions.insight.subtitle}
            description={t.app.forMunicipalities.solutions.insight.description}
            badge={t.app.forMunicipalities.solutions.insight.badge}
            isCard={true}
            planDemoButton={true}
          />
        </LandingSection>

        <LandingSection backgroundColor="var(--palette-pink-100)">
          <LandingContent
            imageSide="right"
            imageSrc={KnowledgeImage}
            title={t.app.forMunicipalities.solutions.knowledge.title}
            subtitle={t.app.forMunicipalities.solutions.knowledge.subtitle}
            description={
              t.app.forMunicipalities.solutions.knowledge.description
            }
            badge={t.app.forMunicipalities.solutions.knowledge.badge}
            isCard={true}
            planDemoButton={true}
          />
        </LandingSection>
      </Column>

      <LandingSection>
        <FaqSection
          items={faqItems}
          isMobile={isMobile}
          title={t.app.forMunicipalities.faq.title}
          subtitle={t.app.forMunicipalities.faq.subtitle}
        />
      </LandingSection>

      <LandingSection>
        <ContactCard />
      </LandingSection>
    </PageLayout>
  )
}
